import React, { useCallback, useEffect, useState } from 'react';
import '../styles/checkout.css';
import { useShoppingCart } from "../hooks/useShoppingCart";
import CartSummary from '../components/CartSummary';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useNavigate } from "react-router-dom";
import { apiRequest } from '../services/apiClient'
import { requestInvoiceByMail, calculateTax } from '../services/apiCalls'
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from "react-google-recaptcha";

if(!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY){
    console.warn('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set')
}
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Checkout = () => <CheckoutForm />;

export default Checkout;

const CheckoutForm = () => {
    const {t,i18n} = useTranslation();
    const { formValues, subscription, addons, submitInformationForm, caseId, isSubmitting, pendingSubmit, interval, taxResult, taxNeedsRecalculation, updateTaxResult } = useShoppingCart();
    const navigate = useNavigate();
    const [finalFormSubmitted, setFinalFormSubmitted] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [isRequestingInvoice, setIsRequestingInvoice] = useState(false);
    const [isCalculatingTax, setIsCalculatingTax] = useState(false);

    const handleRecaptcha = (token) => {
        setCaptchaToken(token)
    };

    const requestInvoice = async () => {
        if (isRequestingInvoice) return;

        setIsRequestingInvoice(true);

        const lineItems = formatLineItems(subscription, addons, formValues);

        try {
            await requestInvoiceByMail({
                captchaToken: captchaToken,
                lineItems,
                email: formValues.billingEmail,
                caseId: caseId,
                street: formValues.street,
                postalCode: formValues.postalCode,
                city: formValues.city,
                country: formValues.countryISO,
                languageCode: i18n.language?.toUpperCase(),
                vatNumber: formValues.vatNumber,
                contactFirstName: formValues.contactFirstName,
                contactLastName: formValues.contactLastName,
                companyName: formValues.companyName
            });

            navigate(
                `/checkout-complete?invoice_sent=true&email=${encodeURIComponent(formValues.billingEmail)}`
            );

        } catch (error) {
            console.error(error);
            setIsRequestingInvoice(false);
        }
    };

    // Callback to create Checkout session
    const fetchClientSecret = useCallback(async () => {
        if (!subscription) {
            navigate('/subscriptions')
        };

        // Prepare all line items: subscription + addons
        const lineItems = formatLineItems(subscription, addons, formValues);
        // Call backend to create Stripe checkout session
        const response = await apiRequest("/api/stripe/create-checkout-session", {
            method: "POST",
            body: {
                lineItems,
                email: formValues.billingEmail,
                caseId: caseId,
                street: formValues.street,
                postalCode: formValues.postalCode,
                city: formValues.city,
                country: formValues.countryISO,
                languageCode: i18n.language?.toUpperCase(),
                vatNumber: formValues.vatNumber,
                contactFirstName: formValues.contactFirstName,
                contactLastName: formValues.contactLastName,
                companyName: formValues.companyName
            },
        });

        return response.clientSecret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues, subscription, addons, caseId]);

    const handleCalculateTax = async () => {
        setIsCalculatingTax(true);

        const lineItems = formatLineItems(subscription, addons, formValues);

        const body = {
            lineItems,
            email: formValues.billingEmail,
            caseId,
            street: formValues.street,
            postalCode: formValues.postalCode,
            city: formValues.city,
            country: formValues.countryISO,
            languageCode: i18n.language?.toUpperCase(),
            vatNumber: formValues.vatNumber,
            contactFirstName: formValues.contactFirstName,
            contactLastName: formValues.contactLastName,
            companyName: formValues.companyName
        };

        try {
            const tax = await calculateTax(body);
            updateTaxResult(tax);
        } finally {
            setIsCalculatingTax(false);
        }
    };

    useEffect(() => {
        async function submitFinalForm() {
            await submitInformationForm()
            setFinalFormSubmitted(true);
        }
        submitFinalForm();
    },[])

    useEffect(() => {
        if (paymentMethod !== "bank transfer") return;
        if (!taxNeedsRecalculation) return;

        handleCalculateTax();
    }, [paymentMethod, taxNeedsRecalculation]);

    if (!subscription) {
        navigate('/subscriptions')
    };

    const stillLoading = isSubmitting || pendingSubmit || !caseId || !finalFormSubmitted;

    if (stillLoading) {
        return (
            <div style={{
            padding: "2rem",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem"
            }}>
                <div
                    className='spinner'
                />
                <h2 style={{color: 'white'}}>{t("preparing_checkout")}</h2>

                {/* Inline <style> to define animation */}
                <style>
                    {`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    `}
                </style>
            </div>
        );
    }

    if (!paymentMethod) {
        return (
            <div style={{ padding: "2rem", maxWidth: 600, margin: "0 auto", color: "white" }}>
                <h2 style={{textAlign: 'center'}}>{t("choose_payment_method")}</h2>

                <div className="d-flex gap-3">
                    <button
                        className="btn btn-primary d-flex align-items-center justify-content-center flex-fill"
                        onClick={() => setPaymentMethod("automatic")}
                    >
                        <i className="bi bi-credit-card-2-front me-2"></i>
                        {t("automatic_payment")}
                    </button>

                    <button
                        className="btn btn-primary d-flex align-items-center justify-content-center flex-fill"
                        onClick={() => setPaymentMethod("bank transfer")}
                        disabled={interval === "month"}
                    >
                        <i className="bi bi-bank me-2"></i>
                        {t("invoice_by_email")}
                    </button>
                </div>
                {interval === "month" && (
                    <div style={{textAlign: 'end', color: 'white', marginRight: '0.25rem'}}>
                        {t("invoice_not_available_for_monthly")}
                    </div>
                )}
            </div>
        );
    }

    if (paymentMethod === "bank transfer") {
        return (
            <div className="checkout-summary">
                <CartSummary
                    subscription={subscription}
                    addons={addons}
                    interval={interval}
                    taxResult={taxResult}
                    isCalculatingTax={isCalculatingTax}
                />

                <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptcha}
                />

                <div style={{textAlign: 'center', color: 'white'}}>{t("invoice_will_be_sent_to")} <strong>{formValues.billingEmail}</strong></div>
                {
                    !isCalculatingTax && <button
                        className="btn btn-primary d-flex align-items-center justify-content-center flex-fill"
                        onClick={requestInvoice}
                        disabled={!captchaToken || isRequestingInvoice}
                    >
                        {isRequestingInvoice ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                {t("processing")}
                            </>
                        ) : (
                            t("request_invoice")
                        )}
                    </button>
                }
            </div>
        )
    }

    if (paymentMethod === "automatic") {
        return (
            <div id="checkout">
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            </div>
        );
    }
};

const formatLineItems = (subscription, addons, formValues) => {
    if (!subscription) return [];

    return [
        {
            priceId: subscription.price.id,
            quantity: subscription.quantity || 1,
            metadata: {
                type: "subscription",
                ...formValues
            }
        },
        ...addons
            .filter(addon => addon.price?.id)
            .map(addon => ({
                priceId: addon.price.id,
                quantity: addon.quantity || 1,
                metadata: {
                    type: "addon",
                    productId: addon.id,
                    contacts: formValues.addonContacts?.[addon.id] || [],
                    accounts: formValues.addonCompanies?.[addon.id] || []
                }
            }))
    ];
};